import React, { useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  TimeScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import {
  CandlestickController,
  CandlestickElement,
} from "chartjs-chart-financial";

ChartJS.register(
  CandlestickController,
  CandlestickElement,
  LinearScale,
  TimeScale,
  Tooltip
);

const RealCandlestickChart = ({ data = [], range = "1D" }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const scrollbarTrackRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startIndex: 0 });
  const [windowSize, setWindowSize] = useState(1);
  const [startIndex, setStartIndex] = useState(0);
  const prevLengthRef = useRef(0);
  const prevRangeRef = useRef(range);

  const totalPoints = data.length;
  const minWindowSize = 10;

  useEffect(() => {
    if (!totalPoints) {
      setWindowSize(1);
      setStartIndex(0);
      prevLengthRef.current = 0;
      return;
    }

    const prevLength = prevLengthRef.current || 0;
    const rangeChanged = prevRangeRef.current !== range;
    const wasFullyZoomedOut = prevLength > 0 && windowSize >= prevLength;
    const prevMaxStart = Math.max(0, prevLength - windowSize);
    const wasViewingLatest = prevLength > 0 && startIndex >= prevMaxStart;
    const nextMaxStart = Math.max(0, totalPoints - windowSize);

    if (rangeChanged || prevLength === 0 || wasFullyZoomedOut) {
      setWindowSize(totalPoints);
      setStartIndex(0);
    } else if (totalPoints > prevLength && wasViewingLatest) {
      // New live candle appended — keep following the latest candles
      setStartIndex(Math.max(0, totalPoints - windowSize));
    } else if (startIndex > nextMaxStart) {
      setStartIndex(nextMaxStart);
    }

    prevRangeRef.current = range;
    prevLengthRef.current = totalPoints;
  }, [totalPoints, range, windowSize, startIndex]);

  useEffect(() => {
    if (!canvasRef.current) return;

    // destroy old chart
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    // 🔥 transform API data → chart format
    const formattedData = data.map((c, index) => ({
        x: index, // ✅ use index instead of timestamp
        rawTime: c.time, // keep original for tooltip
        o: c.open,
        h: c.high,
        l: c.low,
        c: c.close,
      }));

    const visibleMax = Math.min(
      formattedData.length - 1,
      startIndex + Math.max(windowSize - 1, 0)
    );
    const rightPaddingCandles = 10;

    chartRef.current = new ChartJS(canvasRef.current, {
      type: "candlestick",
      data: {
        datasets: [
          {
            label: "Price",
            data: formattedData,
            borderColor: {
              up: "#16A34A",
              down: "#DC2626",
              unchanged: "#64748B",
            },
            color: {
              up: "#22C55E",
              down: "#EF4444",
              unchanged: "#94A3B8",
            },
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        parsing: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
          
            callbacks: {
              title(items) {
                const point = items?.[0]?.raw || {};
          
                let d;
          
                if (point.rawTime) {
                  // ✅ safer parsing
                  const parsed = new Date(point.rawTime);
          
                  if (!isNaN(parsed)) {
                    d = parsed;
                  } else {
                    // fallback if parsing fails
                    d = new Date(point.x);
                  }
                } else {
                  d = new Date(point.x);
                }
          
                return range === "1D"
                  ? d.toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : d.toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                    });
              },
          
              label(context) {
                const p = context.raw;
          
                return [
                  `Open: ₹${p.o}`,
                  `High: ₹${p.h}`,
                  `Low : ₹${p.l}`,
                  `Close: ₹${p.c}`,
                ];
              },
            },
          }
        },
        scales: {
            x: {
                type: "linear",
                min: startIndex,
                max: visibleMax + rightPaddingCandles,
              
                offset: false,          // ✅ remove extra side padding
                bounds: "data",         // ✅ fit exactly to data
              
                ticks: {
                  maxRotation: 0,
                  autoSkip: true,
                  callback: function (val) {
                    const point = formattedData[val];
                    if (!point) return "";
              
                    const d = new Date(point.rawTime);
              
                    return range === "1D"
                      ? d.toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : d.toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        });
                  },
                },
              
                grid: {
                  drawBorder: false,
                },
              },
          y: {
            ticks: {
              callback(val) {
                return `₹${val}`;
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [data, range, startIndex, windowSize]);

  const maxStart = Math.max(0, totalPoints - windowSize);
  const canScroll = maxStart > 0;
  const canZoomIn = windowSize > minWindowSize;
  const canZoomOut = windowSize < totalPoints;
  const thumbWidthPct = totalPoints > 0 ? Math.max((windowSize / totalPoints) * 100, 8) : 100;
  const thumbLeftPct = maxStart > 0 ? (startIndex / maxStart) * (100 - thumbWidthPct) : 0;

  const handleZoomIn = () => {
    setWindowSize((prevWindow) => {
      const nextWindow = Math.max(minWindowSize, Math.floor(prevWindow * 0.8));
      setStartIndex((prevStart) => Math.min(prevStart, Math.max(0, totalPoints - nextWindow)));
      return nextWindow;
    });
  };

  const handleZoomOut = () => {
    setWindowSize((prevWindow) => {
      const nextWindow = Math.min(totalPoints, Math.ceil(prevWindow * 1.25));
      setStartIndex((prevStart) => Math.min(prevStart, Math.max(0, totalPoints - nextWindow)));
      return nextWindow;
    });
  };

  const handleResetView = () => {
    setWindowSize(totalPoints || 1);
    setStartIndex(0);
  };

  const step = Math.max(1, Math.floor(windowSize * 0.25));
  const handlePanLeft = () => setStartIndex((prev) => Math.max(0, prev - step));
  const handlePanRight = () => setStartIndex((prev) => Math.min(maxStart, prev + step));

  const handleTrackClick = (e) => {
    if (!canScroll || !scrollbarTrackRef.current) return;
    const rect = scrollbarTrackRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = rect.width > 0 ? clickX / rect.width : 0;
    const target = Math.round(ratio * maxStart);
    setStartIndex(Math.max(0, Math.min(maxStart, target)));
  };

  const handleThumbMouseDown = (e) => {
    if (!canScroll) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { active: true, startX: e.clientX, startIndex };
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragRef.current.active || !scrollbarTrackRef.current || !canScroll) return;
      const rect = scrollbarTrackRef.current.getBoundingClientRect();
      const thumbWidthPx = Math.max(1, (thumbWidthPct / 100) * rect.width);
      const usableWidth = Math.max(1, rect.width - thumbWidthPx);
      const deltaX = e.clientX - dragRef.current.startX;
      const deltaRatio = deltaX / usableWidth;
      const nextIndex = Math.round(dragRef.current.startIndex + deltaRatio * maxStart);
      setStartIndex(Math.max(0, Math.min(maxStart, nextIndex)));
    };

    const onMouseUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [canScroll, maxStart, thumbWidthPct]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "8px",
          padding: "0 4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <button
          onClick={handleZoomOut}
          disabled={!canZoomOut}
          style={{
            border: "1px solid #CBD5E1",
            borderRadius: "999px",
            padding: "4px 10px",
            fontSize: "12px",
            fontWeight: "600",
            background: canZoomOut ? "#fff" : "#F1F5F9",
            color: canZoomOut ? "#334155" : "#94A3B8",
            cursor: canZoomOut ? "pointer" : "not-allowed",
          }}
        >
          Zoom -
        </button>
        <button
          onClick={handleZoomIn}
          disabled={!canZoomIn}
          style={{
            border: "1px solid #CBD5E1",
            borderRadius: "999px",
            padding: "4px 10px",
            fontSize: "12px",
            fontWeight: "600",
            background: canZoomIn ? "#fff" : "#F1F5F9",
            color: canZoomIn ? "#334155" : "#94A3B8",
            cursor: canZoomIn ? "pointer" : "not-allowed",
          }}
        >
          Zoom +
        </button>
        <button
          onClick={handleResetView}
          style={{
            border: "1px solid #CBD5E1",
            borderRadius: "999px",
            padding: "4px 10px",
            fontSize: "12px",
            fontWeight: "600",
            background: "#fff",
            color: "#334155",
            cursor: "pointer",
          }}
        >
          Reset
        </button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <canvas ref={canvasRef} />
      </div>

      {canScroll && (
        <div style={{ padding: "0 6px 2px", display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            onClick={handlePanLeft}
            style={{
              border: "1px solid #CBD5E1",
              borderRadius: "999px",
              padding: "3px 9px",
              fontSize: "12px",
              fontWeight: "600",
              background: "#fff",
              color: "#334155",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            ◀
          </button>
          <div
            ref={scrollbarTrackRef}
            onClick={handleTrackClick}
            style={{
              position: "relative",
              flex: 1,
              height: "12px",
              borderRadius: "999px",
              background: "#E2E8F0",
              cursor: "pointer",
            }}
          >
            <div
              onMouseDown={handleThumbMouseDown}
              style={{
                position: "absolute",
                top: "1px",
                left: `${thumbLeftPct}%`,
                width: `${thumbWidthPct}%`,
                height: "10px",
                borderRadius: "999px",
                background: "linear-gradient(135deg, #2457BF, #1A42A0)",
                boxShadow: "0 2px 8px rgba(36,87,191,0.35)",
                cursor: "grab",
                transition: dragRef.current.active ? "none" : "left 0.1s ease",
              }}
            />
          </div>
          <button
            onClick={handlePanRight}
            style={{
              border: "1px solid #CBD5E1",
              borderRadius: "999px",
              padding: "3px 9px",
              fontSize: "12px",
              fontWeight: "600",
              background: "#fff",
              color: "#334155",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            ▶
          </button>
        </div>
      )}
    </div>
  );
};

export default RealCandlestickChart;